import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../common/state/store";
import { clearMediaPushJob, getMediaPushJob, IMediaPushJob } from "../state/deviceInstallerSlice";
import { cancelCurrentMediaPush } from "../utils/mediaPushOrchestrator";
import { PROGRESS_SX } from "../constants";
import { deriveMediaPushJobStatus } from "../utils/jobStatus";
import { CheckIcon, CloseIcon, WarningCircleIcon } from "./icons";
import ModalShell from "./ModalShell";
import StepperProgress from "./StepperProgress";
import LinearProgress from "@mui/material/LinearProgress";

interface Props {
  onClose: () => void;
}

const STEPS = ["fetchList", "pushFiles", "done"] as const;
type Step = typeof STEPS[number];

const STEP_LABEL_KEYS: Record<Step, string> = {
  fetchList: "deviceInstaller.stepFetchList",
  pushFiles: "deviceInstaller.stepPushFiles",
  done: "deviceInstaller.stepDone",
};

function getStepIndex(status: IMediaPushJob["status"]): number {
  if (status === "fetching_manifest") return 0;
  if (status === "pushing") return 1;
  if (status === "success") return STEPS.length;
  return -1;
}

const MediaPushProgressModal = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const job = useSelector(getMediaPushJob);

  if (!job) return null;

  const jobStatus = deriveMediaPushJobStatus(job);
  const { isCancelled, isSuccess, isError, isDone } = jobStatus;
  const canCancel = job.status === "fetching_manifest" || job.status === "pushing";

  const handleClose = () => {
    dispatch(clearMediaPushJob());
    onClose();
  };

  const handleCancel = () => {
    cancelCurrentMediaPush();
  };

  const rawStepIndex = getStepIndex(job.status);
  const currentStepIndex = rawStepIndex >= 0 ? rawStepIndex : 0;

  const processedFiles = job.pushedFiles + (job.skippedFiles || 0);
  const overallProgress =
    job.totalFiles > 0 ? Math.round((processedFiles / job.totalFiles) * 100) : 0;

  return (
    <ModalShell
      title={t("deviceInstaller.mediaPushTitle")}
      width="28rem"
      bodyHeight="280px"
      onClose={isDone ? handleClose : handleCancel}
    >
      {/* Stepper */}
      <StepperProgress
        steps={STEPS}
        stepLabelKeys={STEP_LABEL_KEYS}
        currentStepIndex={currentStepIndex}
        jobStatus={jobStatus}
      />

      {/* Dynamic content area — fills remaining space, vertically centered */}
      <div className="flex-1 flex flex-col justify-center min-h-0">
        {/* Progress section */}
        {!isDone && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-500">
                {job.status === "fetching_manifest" &&
                  t("deviceInstaller.mediaPushFetchingManifest")}
                {job.status === "pushing" && t("deviceInstaller.mediaPushPushing")}
              </span>
              {job.status === "pushing" && job.totalFiles > 0 && (
                <span className="text-xs text-gray-500">
                  {t("deviceInstaller.mediaPushProgress", {
                    pushed: job.pushedFiles,
                    total: job.totalFiles,
                  })}
                </span>
              )}
            </div>

            {job.status === "fetching_manifest" ? (
              <LinearProgress sx={PROGRESS_SX} />
            ) : (
              <>
                {/* Overall progress */}
                <LinearProgress variant="determinate" value={overallProgress} sx={PROGRESS_SX} />
                {/* Current file info */}
                {job.currentFileName && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 truncate">
                      {t("deviceInstaller.mediaPushCurrentFile", { name: job.currentFileName })}
                    </p>
                    <LinearProgress
                      variant="determinate"
                      value={job.currentFileProgress}
                      sx={{ ...PROGRESS_SX, height: 3, mt: 0.5 }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Success */}
        {isSuccess && (
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-green-50">
              <CheckIcon className="w-8 h-8 text-green-500" />
            </div>
            <span className="text-sm font-medium text-green-500">
              {t("deviceInstaller.mediaPushSuccess")}
            </span>
            {(job.skippedFiles > 0 || job.failedFiles > 0) && (
              <div className="flex flex-col items-center gap-1">
                {job.skippedFiles > 0 && (
                  <span className="text-xs text-gray-500">
                    {t("deviceInstaller.mediaPushSkipped", { count: job.skippedFiles })}
                  </span>
                )}
                {job.failedFiles > 0 && (
                  <span className="text-xs text-orange-500">
                    {t("deviceInstaller.mediaPushSuccessWithFailures", {
                      failed: job.failedFiles,
                    })}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cancelled */}
        {isCancelled && (
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
            <CloseIcon className="w-5 h-5 text-gray-400 shrink-0" />
            <span className="text-sm text-gray-500">{t("deviceInstaller.mediaPushCancelled")}</span>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="flex items-start gap-2 bg-red-50 rounded-lg p-3">
            <WarningCircleIcon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">
              {t("deviceInstaller.mediaPushError", {
                error: job.errorMessage || "Unknown error",
              })}
            </p>
          </div>
        )}
      </div>

      {/* Footer buttons — always at bottom */}
      <div className="flex items-center justify-end gap-2 mt-4 shrink-0">
        {canCancel && (
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t("deviceInstaller.cancel")}
          </button>
        )}
        {isDone && (
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-white bg-green-500 rounded-lg hover:opacity-90 transition-opacity"
          >
            {t("general.close")}
          </button>
        )}
      </div>
    </ModalShell>
  );
};

export default MediaPushProgressModal;
